import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../../interceptors/api";
import { DeviceStoryDto } from "../../dto/DeviceStoryDto";

import "./styles/IPodMiniPlayerWindow.css";
import "./styles/DesktopWindow.css";

function IpodMiniPlayerWindow() {
  const clickAudioRef = useRef<HTMLAudioElement>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const playClickSound = useCallback(() => {
    if (clickAudioRef.current) {
      clickAudioRef.current.currentTime = 0; // Reset to start
      clickAudioRef.current.play().catch(() => {
        // Ignore errors if audio can't play
      });
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchDeviceStory = () => {
      api
        .get<DeviceStoryDto>("/api/device-story/device/2/exhibition/1")
        .then((res) => {
          if (!isMounted) return;
          if (res.data.audioUrl) {
            setAudioUrl(res.data.audioUrl);
            setError(null);
          } else {
            setError("No device story available.");
          }
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error("Error loading device story", err);
          setError("Failed to load device story.");
        });
    };

    // Fetch immediately on mount
    fetchDeviceStory();

    // Fetch device story every minute
    const intervalId = window.setInterval(fetchDeviceStory, 60_000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const togglePlay = () => {
    playClickSound();

    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const onLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;
    const t = parseFloat(e.target.value);
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const onVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setVolume(parseFloat(e.target.value));
    },
    [],
  );

  const progressFill = duration
    ? `${Math.floor((currentTime / duration) * 100)}%`
    : "0%";

  return (
    <div className="osx-desktop-window itunes-mini-player-window">
      <div className="osx-desktop-window-titlebar itunes-mini-player-titlebar">
        <div className="osx-desktop-window-dots">
          <span className="dot red" />
          <span className="dot yellow" />
          <span className="dot green" />
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {!error && audioUrl && (
        <>
          <div className="itunes-mini-player-controls">
            <div className="itunes-mini-player-play-pause">
              <button
                className="mini-play-btn"
                onClick={togglePlay}
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? "❚❚" : "▶"}
              </button>
            </div>
            <div className="itunes-mini-player-volume">
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                defaultValue={1}
                onChange={onVolumeChange}
                className="volume-slider"
              />
            </div>
            <audio
              ref={clickAudioRef}
              src="/assets/themes/ipod/audio/click.mp3"
              preload="auto"
            />
          </div>

          <div className="itunes-mini-player-content">
            <div className="itunes-mini-player-song">iPod Device Story</div>
            <div className="itunes-mini-player-artist">HSLU Werkschau 2025</div>

            <audio
              ref={audioRef}
              src={audioUrl}
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={onTimeUpdate}
            />

            <div className="itunes-mini-player-progress">
              <span className="progress-time">
                {new Date(currentTime * 1000)
                  .toISOString()
                  .slice(14, 19)
                  .slice(1)}
              </span>
              <input
                type="range"
                min={0}
                max={duration}
                value={currentTime}
                onChange={onSeek}
                className="progress-slider"
                style={
                  {
                    "--fill-percent": progressFill,
                  } as React.CSSProperties
                }
              />
              <span className="progress-time">
                {new Date(duration * 1000).toISOString().slice(14, 19).slice(1)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default IpodMiniPlayerWindow;
